import React, { useEffect, useRef, useState } from 'react';
import styled from '../../styled';

const GradientBg = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    #fff5f0 0%,
    #f8f0cf 30%,
    #ffffff 60%,
    #fff8f5 100%
  );
  z-index: 0;
`;

const SvgBlur = styled('svg')`
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const NoiseBg = styled('svg')`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
  mix-blend-mode: soft-light;
  opacity: 0.1;
  pointer-events: none;
`;

const GradientsContainer = styled('div')`
  @keyframes moveInCircle {
    0% {
      transform: rotate(0deg);
    }
    50% {
      transform: rotate(180deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes moveVertical {
    0% {
      transform: translateY(-70%);
    }
    50% {
      transform: translateY(70%);
    }
    100% {
      transform: translateY(-70%);
    }
  }

  @keyframes moveHorizontal {
    0% {
      transform: translateX(-70%) translateY(-15%);
    }
    50% {
      transform: translateX(70%) translateY(15%);
    }
    100% {
      transform: translateX(-70%) translateY(-15%);
    }
  }

  filter: url(#goo) blur(40px);
  width: 100%;
  height: 100%;
  position: relative;
`;

const GradientBlob = styled('div')<{
  color: string;
  size?: string;
  top?: string;
  left?: string;
  transformOrigin?: string;
  animationType: 'circle' | 'vertical' | 'horizontal';
  animationDuration: string;
  opacity?: number;
}>`
  position: absolute;
  background: radial-gradient(
    circle at center,
    ${(p) => p.color} 0%,
    ${(p) => p.color.replace(/[\d.]+\)$/, '0)')} 50%
  );
  mix-blend-mode: hard-light;
  width: ${(p) => p.size || '80%'};
  height: ${(p) => p.size || '80%'};
  top: ${(p) => p.top || 'calc(50% - 40%)'};
  left: ${(p) => p.left || 'calc(50% - 40%)'};
  transform-origin: ${(p) => p.transformOrigin || 'center center'};
  animation: ${(p) => {
      if (p.animationType === 'circle') return 'moveInCircle';
      if (p.animationType === 'vertical') return 'moveVertical';
      return 'moveHorizontal';
    }}
    ${(p) => p.animationDuration} ease infinite;
  opacity: ${(p) => p.opacity ?? 1};
  pointer-events: none;
`;

const InteractiveBlob = styled('div')<{
  translateX: number;
  translateY: number;
}>`
  position: absolute;
  background: radial-gradient(
    circle at center,
    rgba(255, 107, 53, 0.25) 0%,
    rgba(255, 107, 53, 0) 50%
  );
  mix-blend-mode: hard-light;
  width: 100%;
  height: 100%;
  top: -50%;
  left: -50%;
  opacity: 0.5;
  transform: translate(${(p) => p.translateX}px, ${(p) => p.translateY}px);
  pointer-events: none;
`;

export const GradientBackground = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentPosition, setCurrentPosition] = useState({ x: 0, y: 0 });
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setMousePosition({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);

    const move = () => {
      setCurrentPosition((prev) => {
        const newX = prev.x + (mousePosition.x - prev.x) / 12;
        const newY = prev.y + (mousePosition.y - prev.y) / 12;
        return { x: newX, y: newY };
      });
      animationFrameRef.current = requestAnimationFrame(move);
    };

    animationFrameRef.current = requestAnimationFrame(move);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [mousePosition.x, mousePosition.y]);

  return (
    <GradientBg>
      <SvgBlur xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="goo">
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="10"
              result="blur"
            />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8"
              result="goo"
            />
            <feBlend in="SourceGraphic" in2="goo" />
          </filter>
        </defs>
      </SvgBlur>

      <NoiseBg viewBox="0 0 100vw 100vw" xmlns="http://www.w3.org/2000/svg">
        <filter id="noiseFilterBg">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.6"
            stitchTiles="stitch"
          />
        </filter>
        <rect
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          filter="url(#noiseFilterBg)"
        />
      </NoiseBg>

      <GradientsContainer>
        <GradientBlob
          color="rgba(255, 107, 53, 0.2)"
          animationType="vertical"
          animationDuration="20s"
        />
        <GradientBlob
          color="rgba(255, 200, 87, 0.18)"
          transformOrigin="calc(50% - 400px)"
          animationType="circle"
          animationDuration="15s"
        />
        <GradientBlob
          color="rgba(255, 150, 100, 0.18)"
          top="calc(50% - 40% + 200px)"
          left="calc(50% - 40% - 500px)"
          transformOrigin="calc(50% + 400px)"
          animationType="circle"
          animationDuration="25s"
        />
        <GradientBlob
          color="rgba(255, 180, 120, 0.15)"
          transformOrigin="calc(50% - 200px)"
          animationType="horizontal"
          animationDuration="25s"
          opacity={0.8}
        />
        <GradientBlob
          color="rgba(255, 107, 53, 0.12)"
          size="160%"
          top="calc(50% - 80%)"
          left="calc(50% - 80%)"
          transformOrigin="calc(50% - 800px) calc(50% + 200px)"
          animationType="circle"
          animationDuration="15s"
        />
        <InteractiveBlob
          translateX={currentPosition.x}
          translateY={currentPosition.y}
        />
      </GradientsContainer>
    </GradientBg>
  );
};
